// "A new version is ready" (docs/APP-UPDATES.md, "Back office and web"). Pure: no fetch, and the DOM
// only through what is passed in. The running page knows its own { version, build } (baked in by
// scripts/vite-version.mjs); the server's /version.json says what is deployed now.
// Every update is required and always on top (owner, 2026-10-14): any new deploy blocks the page
// until it refreshes, and nobody can put it off.
import { isSemver } from '@popcart/core/v1';

/** @typedef {{ version: string, build: string }} BuildInfo */
/** @typedef {{ urgency: 'required', version: string, build: string }} VersionPrompt */

/**
 * What to show for a /version.json answer.
 *   null        nothing (same build, or an unreadable answer)
 *   required    a different build (a new version, a rollback, or a redeploy of the same version):
 *               the blocking overlay with Refresh only
 * @param {BuildInfo} running
 * @param {unknown} latest the parsed /version.json (anything; it comes off the network)
 * @returns {VersionPrompt | null}
 */
export function versionPrompt(running, latest) {
  if (!latest || typeof latest !== 'object') return null;
  const { version, build } = /** @type {{ version?: unknown, build?: unknown }} */ (latest);
  if (typeof build !== 'string' || !build || typeof version !== 'string' || !isSemver(version)) return null;
  if (build === running.build) return null;
  return { urgency: 'required', version, build };
}

/**
 * While the overlay is up, the app under it can't be reached: `inert` (no focus, no clicks, no find)
 * and hidden from assistive tech. The overlay itself lives outside `root` (a portal on <body>).
 * @param {{ inert: boolean, setAttribute(n: string, v: string): void, removeAttribute(n: string): void } | null} root
 * @param {boolean} blocked
 */
export function setPageBlocked(root, blocked) {
  if (!root) return;
  root.inert = blocked;
  if (blocked) root.setAttribute('aria-hidden', 'true');
  else root.removeAttribute('aria-hidden');
}

/**
 * Escape never closes the overlay: a capture-phase keydown handler that swallows it.
 * @param {{ key: string, preventDefault(): void, stopPropagation(): void }} e
 */
export function swallowEscape(e) {
  if (e.key !== 'Escape') return;
  e.preventDefault();
  e.stopPropagation();
}

/** How often the page asks (also on every return to the tab). */
export const VERSION_POLL_MS = 5 * 60_000;
